import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, ChevronDown } from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';

const CATEGORY_COLORS = {
  court: '#EF4444', hearing: '#EF4444', court_appearance: '#EF4444',
  deadline: '#F97316', trial: '#DC2626',
  meeting: '#0EA5E9', consultation: '#8B5CF6',
  deposition: '#06B6D4', mediation: '#6366F1',
};

const FILTER_OPTIONS = [
  { key: 'all', label: 'All', color: '#6B7280' },
  { key: 'court', label: 'Court Dates', color: '#EF4444', cats: ['court', 'hearing', 'court_appearance', 'trial'] },
  { key: 'deadline', label: 'Deadlines', color: '#F97316', cats: ['deadline'] },
  { key: 'meeting', label: 'Meetings', color: '#0EA5E9', cats: ['meeting', 'consultation'] },
];

const PortalCalendar = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [daysAhead, setDaysAhead] = useState(90);
  const [filterCategory, setFilterCategory] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    setLoading(true);
    clientPortalService.getCalendarEvents(null, daysAhead)
      .then(data => setEvents(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [daysAhead]);

  const filtered = filterCategory === 'all'
    ? events
    : events.filter(e => {
        const opt = FILTER_OPTIONS.find(f => f.key === filterCategory);
        return opt?.cats?.includes((e.category || '').toLowerCase());
      });

  // Group by month
  const grouped = {};
  filtered.forEach(e => {
    const d = new Date(e.start_time);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
    if (!grouped[key]) grouped[key] = { label, events: [] };
    grouped[key].events.push(e);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900">
      {/* Header */}
      <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center space-x-4">
          <button
            onClick={() => navigate('/portal/dashboard')}
            className="text-virgo-teal-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex items-center space-x-3">
            <Calendar className="text-cyan-400" size={24} />
            <div>
              <h1 className="text-xl font-bold text-white">My Calendar</h1>
              <p className="text-sm text-virgo-teal-400">Upcoming events across all your matters</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filter Pills */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {FILTER_OPTIONS.map(opt => {
            const active = filterCategory === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setFilterCategory(opt.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border ${
                  active
                    ? 'text-white border-transparent'
                    : 'text-virgo-teal-300 border-virgo-teal-700/30 bg-virgo-navy-800/50 hover:border-virgo-teal-500/50'
                }`}
                style={active ? { background: opt.color, borderColor: opt.color } : {}}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-12 text-center">
            <Calendar className="text-virgo-teal-500 mx-auto mb-4" size={48} />
            <h2 className="text-xl font-semibold text-white mb-2">No Upcoming Events</h2>
            <p className="text-virgo-teal-400 mb-4">No events scheduled in the next {daysAhead} days.</p>
            <Link
              to="/portal/appointments"
              className="inline-block bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Book an Appointment
            </Link>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([key, group]) => (
              <div key={key}>
                <h3 className="text-xs font-bold text-virgo-teal-500 tracking-widest mb-3">{group.label}</h3>
                <div className="space-y-2">
                  {group.events.map((event, idx) => {
                    const evDate = new Date(event.start_time);
                    const cat = (event.category || 'other').toLowerCase();
                    const dotColor = CATEGORY_COLORS[cat] || '#6B7280';
                    const isExpanded = expandedId === event.id;

                    return (
                      <div
                        key={event.id || idx}
                        className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 overflow-hidden cursor-pointer hover:border-virgo-teal-500/50 transition-all"
                        style={{ borderLeftWidth: 3, borderLeftColor: dotColor }}
                        onClick={() => setExpandedId(isExpanded ? null : event.id)}
                      >
                        <div className="p-4 flex items-start gap-4">
                          {/* Date Column */}
                          <div className="text-center min-w-[50px]">
                            <p className="text-xs text-virgo-teal-500 uppercase">
                              {evDate.toLocaleDateString('en-US', { weekday: 'short' })}
                            </p>
                            <p className="text-2xl font-bold text-white">{evDate.getDate()}</p>
                            <p className="text-xs text-virgo-teal-500">
                              {event.all_day ? 'All day' : evDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </p>
                          </div>
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] text-virgo-teal-500 uppercase tracking-wider font-medium">
                              {cat.replace(/_/g, ' ')}
                            </p>
                            <p className="text-white font-semibold truncate">{event.title}</p>
                            {event.location && (
                              <p className="text-xs text-virgo-teal-400 flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />{event.location}
                              </p>
                            )}
                            {event.case_name && (
                              <p className="text-xs text-virgo-teal-500 mt-1">{event.case_name}</p>
                            )}
                          </div>
                          <ChevronDown
                            className={`text-virgo-teal-500 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                            size={16}
                          />
                        </div>
                        {isExpanded && event.description && (
                          <div className="px-4 pb-4 pt-0 border-t border-virgo-teal-700/20 mt-0">
                            <p className="text-sm text-virgo-teal-300 mt-3">{event.description}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load More */}
        {daysAhead < 180 && filtered.length > 0 && (
          <div className="text-center mt-8">
            <button
              onClick={() => setDaysAhead(180)}
              className="text-cyan-400 hover:text-cyan-300 text-sm font-medium"
            >
              Load more events (next 6 months) &rarr;
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default PortalCalendar;
